'use client';

import React, { useState } from 'react';
import { QuickCalculatorForm } from '@/components/quick-calculator/QuickCalculatorForm';
import { calculateDasha, DashaTimelineResponse, CreateBirthProfileInput } from '@/lib/api/client';
import { CalculatorPageContent } from '@/components/calculators/CalculatorPageContent';
import { Clock, Star, Calendar, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';

export default function DashaCalculatorPage() {
  const [result, setResult] = useState<DashaTimelineResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedMahadasha, setExpandedMahadasha] = useState<string | null>(null);

  const handleCalculate = async (input: CreateBirthProfileInput) => {
    setLoading(true);
    setError(null);
    try {
      const data = await calculateDasha(input);
      setResult(data);
      if (data.current_mahadasha) {
        setExpandedMahadasha(data.current_mahadasha.lord);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to calculate Dasha timeline');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const introParagraph = "Vimshottari Dasha is the premier 120-year planetary timeline system in Parashari Vedic astrology. It maps your life progression through major planetary eras (Mahadasha) and sub-periods (Antardasha) based on your exact Moon Nakshatra at birth.";

  const methodologyContent = (
    <div className="space-y-3">
      <p>
        I calculate your Vimshottari Dasha timeline by determining the exact natal Moon degree in your birth Nakshatra using Swiss Ephemeris (pyswisseph) under Lahiri Ayanamsha.
      </p>
      <p>
        The unelapsed arc within your birth star sets your starting Dasha balance, followed by the fixed 120-year cycle of 9 Graha periods: Sun (6 yrs), Moon (10 yrs), Mars (7 yrs), Rahu (18 yrs), Jupiter (16 yrs), Saturn (19 yrs), Mercury (17 yrs), Ketu (7 yrs), and Venus (20 yrs).
      </p>
    </div>
  );

  const subsections = [
    {
      title: "The 120-Year Vimshottari Cycle",
      content: "Vimshottari means 120 in Sanskrit. The total duration of all 9 planetary Mahadashas equals 120 solar years, progressing in fixed Nakshatra order."
    },
    {
      title: "Mahadasha vs Antardasha",
      content: "The Mahadasha sets the macro environmental theme of a life chapter, while the Antardasha sub-period refines the specific events, timing, and focal areas."
    },
    {
      title: "Starting Balance of Dasha",
      content: "Your birth Moon's degree within your Nakshatra determines how many years of your first Mahadasha were remaining at the exact moment of birth."
    }
  ];

  const faqs = [
    {
      q: "What is a Vimshottari Dasha?",
      a: "Vimshottari Dasha is a 120-year planetary period system in Vedic astrology that maps life events according to your birth Moon Nakshatra."
    },
    {
      q: "How many planets have Dasha periods?",
      a: "All 9 classical Grahas (Sun, Moon, Mars, Rahu, Jupiter, Saturn, Mercury, Ketu, Venus) have fixed Mahadasha durations totaling 120 years."
    },
    {
      q: "What is an Antardasha?",
      a: "An Antardasha is a sub-period within a major Mahadasha. Each Mahadasha contains 9 Antardashas ruled sequentially by all 9 planets."
    },
    {
      q: "Why is the birth Moon position so critical for Dasha?",
      a: "Because the starting planet and initial balance of years depend directly on the Moon's exact arc degree within its birth star."
    },
    {
      q: "Is this Dasha Calculator free?",
      a: "Yes, 100%! All calculations on AstroPredict are completely free."
    }
  ];

  const relatedCalculators = [
    {
      title: "Birth Chart Calculator",
      href: "/birth-chart-calculator",
      description: "Generate your full Kundli with planetary house positions.",
      categoryBadge: "Core"
    },
    {
      title: "Nakshatra Calculator",
      href: "/nakshatra-calculator",
      description: "Find your birth star and Pada determining your initial Dasha lord.",
      categoryBadge: "Lunar"
    },
    {
      title: "Shadbala Calculator",
      href: "/shadbala-calculator",
      description: "Check the 6-fold strength score of your active Dasha lord.",
      categoryBadge: "Strength"
    }
  ];

  return (
    <div className="space-y-8 max-w-4xl mx-auto py-4">
      <QuickCalculatorForm
        title="Vimshottari Dasha Timeline Calculator"
        subtitle="Calculate your complete 120-year planetary Dasha timeline (Mahadasha & Antardasha sequences) derived from your exact birth Moon position."
        onCalculate={handleCalculate}
        loading={loading}
      />

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-300 text-sm">
          {error}
        </div>
      )}

      {result && (
        <div className="bg-astro-900 border border-gray-800 rounded-2xl p-6 shadow-xl space-y-4">
          <h2 className="text-xl font-serif font-bold text-gray-100">
            Vimshottari Dasha Timeline Calculated (120 Years)
          </h2>
        </div>
      )}

      <CalculatorPageContent
        calculatorName="Vimshottari Dasha Calculator"
        introParagraph={introParagraph}
        methodologyTitle="How I Calculate Your Dasha Timeline"
        methodologyContent={methodologyContent}
        subsections={subsections}
        faqs={faqs}
        relatedCalculators={relatedCalculators}
        showSignPeriodGrid={false}
      />
    </div>
  );
}
